import React, { useEffect } from 'react';
import '../styles/ServicesPage.css';
import '../styles/ServicePortfolio.css';
import { Link } from 'react-router-dom';

// Service data with details for each service - Updated to match allServicesData
const services = [
  {
    id: 'cloud-cost-optimization',
    title: 'Cloud Cost Optimization & FinOps',
    category: 'FinOps',
    description: 'Maximize cloud ROI with expert-led FinOps strategies achieving 30-60% cost reduction without sacrificing performance.',
    highlights: ['30-60% Cost Reduction', 'Real-time Monitoring', 'Team-based Governance', 'Kubernetes Cost Breakdown'],
    icon: '💰'
  },
  {
    id: 'kubernetes-consulting',
    title: 'Kubernetes Consulting',
    category: 'Infrastructure',
    description: 'Expert Kubernetes design, deployment, and operations for robust container platforms driving agility, scalability, and resilience.',
    highlights: ['Multi-Regional Architecture', 'Production-Grade Clusters', 'Migration & Modernization', 'Day-2 Operations'],
    icon: '⚙️'
  },
  {
    id: 'terraform-pulumi',
    title: 'Terraform & Pulumi Consulting',
    category: 'Infrastructure',
    description: 'Infrastructure-as-Code services leveraging Terraform and Pulumi to automate, version, and scale cloud infrastructure.',
    highlights: ['Modular Templates', 'Multi-Cloud Expertise', 'CI/CD Integration', 'Drift Detection'],
    icon: '🏗️'
  },
  {
    id: 'sre-consulting',
    title: 'SRE Consulting Services',
    category: 'DevOps',
    description: 'Site Reliability Engineering built for scale, speed, and stability using modern SRE practices and automation.',
    highlights: ['Production Readiness', 'Observability Architecture', 'Incident Management', 'Release Engineering'],
    icon: '🔧'
  },
  {
    id: 'prometheus-grafana',
    title: 'Prometheus & Grafana Consulting',
    category: 'Observability',
    description: 'Turnkey observability platform with real-time metrics, advanced alerting, and rich dashboards for proactive troubleshooting.',
    highlights: ['High-Scale Deployments', 'SLO Management', 'Long-Term Storage', 'Multi-Cloud Monitoring'],
    icon: '📊'
  },
  {
    id: 'policy-as-code',
    title: 'Policy-as-Code Services',
    category: 'Security',
    description: 'Codify governance and secure infrastructure with automated policy enforcement across infrastructure, Kubernetes, and CI/CD pipelines.',
    highlights: ['Multi-Layered Coverage', 'OPA & Kyverno', 'CI/CD Guardrails', 'Compliance Automation'],
    icon: '📋'
  },
  {
    id: 'gitops-consulting',
    title: 'GitOps Consulting & Implementation',
    category: 'DevOps',
    description: 'Build reliable, auditable, and fast software delivery with GitOps principles using modern tools and best practices.',
    highlights: ['Declarative Everything', 'Progressive Delivery', 'Multi-Cluster Deployment', 'Security Built-In'],
    icon: '🔄'
  },
  {
    id: 'infrastructure-design',
    title: 'Infrastructure Design & Delivery',
    category: 'Infrastructure',
    description: 'End-to-end infrastructure design architected for least cost, maximum security, and reliability. Built for scalability and growth.',
    highlights: ['Cloud-Native Architecture', 'Secure Foundations', 'Kubernetes Platform', 'Cost Optimization'],
    icon: '🏛️'
  },
  {
    id: 'cloud-migration',
    title: 'Cloud Migration',
    category: 'Infrastructure',
    description: 'Comprehensive migration services for moving workloads smoothly and securely between cloud environments with minimal downtime.',
    highlights: ['Multi-Cloud Migration', 'VM to Container', 'Database Migration', 'Zero-Downtime Cutover'],
    icon: '☁️'
  },
  {
    id: 'cloud-compliance',
    title: 'Cloud Compliance & Security Readiness',
    category: 'Compliance',
    description: 'End-to-end compliance readiness for SOC 2, HIPAA, GDPR, ISO 27001, and PCI-DSS without relying on expensive SaaS solutions.',
    highlights: ['SOC 2 Type I & II', 'Infrastructure Hardening', 'Policy Automation', 'Direct Auditor Support'],
    icon: '🛡️'
  },
  {
    id: 'ci-cd-services',
    title: 'CI/CD Services',
    category: 'DevOps',
    description: 'End-to-end automation framework that accelerates software delivery, data integration, and ML model deployment with GitOps workflows.',
    highlights: ['GitOps Workflows', 'Progressive Delivery', 'Platform Engineering', 'DevSecOps Integration'],
    icon: '🚀'
  },
  {
    id: 'backup-disaster-recovery',
    title: 'Backup & Disaster Recovery',
    category: 'Infrastructure',
    description: 'Cloud-native, Kubernetes-aware backup and disaster recovery strategies that align with business continuity goals and compliance requirements.',
    highlights: ['Kubernetes-Aware Backups', 'Multi-Region DR', 'Compliance-Ready Workflows', 'Chaos Engineering'],
    icon: '💾'
  },
  {
    id: 'vulnerability-assessment',
    title: 'Vulnerability Assessment & Penetration Testing',
    category: 'Security',
    description: 'Comprehensive security assessments designed to uncover vulnerabilities across networks, applications, and infrastructure using industry-standard methodologies.',
    highlights: ['Black/Gray/White Box Testing', 'OWASP & NIST Standards', 'CVSS Severity Ratings', 'Zero Disruption Testing'],
    icon: '🔍'
  }
];

const ServicesPage: React.FC = () => {

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="services-page py-10 p-8 md:p-12">
      <div className="page-header">
        <h2 className="text-4xl md:text-5xl font-bold animate-fade-in">
          Our Services
        </h2>
      </div>

      {/* Services Overview */}
      <div className="services-overview">
        <div className="overview-content">
          <h3 className="section-title">Expert Solutions for Modern Challenges</h3>
          <p className="overview-text">
            At Betaque, we offer comprehensive technology services designed to transform your business. 
            Our expertise spans across cloud infrastructure, cybersecurity, DevOps, and compliance 
            to deliver end-to-end solutions tailored to your unique needs.
          </p>
          <p className="overview-text">
            We believe in a collaborative approach, working closely with you to understand your 
            challenges and develop strategies that drive measurable results and long-term success.
          </p>
        </div>
      </div>

      {/* Service Highlights Grid - Using New Isolated CSS */}
      <div className="betaque-service-portfolio">
        <h3 className="betaque-service-portfolio-title">Our Service Portfolio</h3>
        <div className="betaque-services-grid">
          {services.map((service) => (
            <Link 
              key={service.id} 
              to={`/services/${service.id}`} 
              className="betaque-service-card-wrapper"
            >
              <div className="betaque-service-card-header">
                <div className="betaque-service-icon">{service.icon}</div>
                <div className="betaque-service-category-badge">{service.category}</div>
              </div>
              
              <div className="betaque-service-card-content">
                <h4 className="betaque-service-card-title">{service.title}</h4>
                <p className="betaque-service-card-description">{service.description}</p>
                
                <div className="betaque-service-highlights-list">
                  {service.highlights.map((highlight, index) => (
                    <span key={index} className="betaque-highlight-badge">
                      {highlight}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="betaque-service-card-footer">
                <span className="betaque-learn-more-link">
                  Learn More <span className="betaque-arrow">→</span>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Service Process */}
      <div className="service-process">
        <h3 className="section-title">Our Approach</h3>
        <div className="services-process-steps">
          <div className="services-process-step">
            <div className="services-step-number">01</div>
            <h4 className="services-step-title">Discovery</h4>
            <p className="services-step-description">
              We begin by understanding your business goals, challenges, and requirements through 
              in-depth consultations.
            </p>
          </div>

          <div className="services-process-step">
            <div className="services-step-number">02</div>
            <h4 className="services-step-title">Strategy</h4>
            <p className="services-step-description">
              Based on our findings, we develop a comprehensive strategy and roadmap aligned 
              with your objectives.
            </p>
          </div>

          <div className="services-process-step">
            <div className="services-step-number">03</div>
            <h4 className="services-step-title">Implementation</h4>
            <p className="services-step-description">
              Our expert team executes the solution with precision, ensuring minimal disruption 
              to your operations.
            </p>
          </div>

          <div className="services-process-step">
            <div className="services-step-number">04</div>
            <h4 className="services-step-title">Optimization</h4>
            <p className="services-step-description">
              We continuously monitor and optimize the solution to ensure it meets your evolving 
              needs and delivers maximum value.
            </p>
          </div>
        </div>
      </div>

    </div>
  );
};

export default ServicesPage;